//Componentes
import TooltipItem from "@/frontend/componentes/TooltipItem";

//Common
import {
  MAX_QTDE_ARQUIVOS_PAG_ENVIO,
  SIGLA_FORMA_PAGAMENTO,
  SITUACAO_PAGAMENTO,
  SITUACAO_SOLICITACAO,
  TIPO_CENTRO_CUSTO,
  TIPO_FILIAL,
  TIPO_LOG_APROVACAO,
  TIPO_PAGAMENTO,
  TIPO_RATEIO,
  TIPO_SOLICITACAO,
  TIPO_VALOR,
  TIPO_VENCIMENTO,
} from "@/common/constantes";
import {
  ERRO_MES_ANO_REF,
  fn_getSituacaoConferencia,
  fn_getSituacaoPagamento,
  fn_getSituacaoReajuste,
  fn_getSituacaoSolicitacao,
  fn_getTipoPagamento,
  fn_getTipoSolicitacao,
  fn_isPagamentoBloqueadoValoresCorrecao,
  fn_loadCamposLogAprovacao,
  fn_permiteAlterarMesAnoRefCorrecao,
  fn_loadOpcoesSituacaoSolicitacao,
  fn_loadOpcoesTipoApropriacao,
  fn_loadOpcoesTipoSolicitacao,
  fn_possuiAlgumaContaReceita,
  fn_possuiSomenteContaPagamento,
  fn_possuiSomenteContaReceita,
  fn_getTipoCentroCusto,
} from "@/common/siig";
import {
  fn_addDay,
  fn_addMonth,
  fn_dataIsAfter,
  fn_dataIsBefore,
  fn_getData,
  fn_getDataAtual,
  fn_getDataInicioPosicaoDia,
  fn_getDifDiasDatas,
  fn_getMesAnoData,
  fn_getUltimoDiaMes,
  fn_validarData,
} from "@/common/utils/date";
import {
  fn_calcularTotal,
  fn_formatarNumero,
  fn_getTotaisCamposResult,
  fn_isEmpty,
} from "@/common/utils/util";

//Util
import {
  fn_getCentrosRateioPagamentos,
  fn_getRateioPagamento,
  fn_getSituacaoRateioPagamento,
} from "./rateio";
import {
  fn_getAreasUsuario,
  fn_getTiposSolicitacaoUsuario,
  fn_getUsuario,
} from "../sessao";
import { fn_getIconeTipoSolicitacao, ICONES_SIIG } from "../icones";
import {
  getMensagemErro,
  MENSAGENS_ERRO,
  showMensagemAviso,
  showMensagemErro,
} from "../mensagens";

//API
import { api_loadOpcoes, ROTA_LOAD } from "@/frontend/services/api/api_load";
import { api_findUniqueAlcada } from "@/frontend/services/api/api_find";
import { api_listar, ROTA_LISTAR } from "@/frontend/services/api/api_list";

//Abas da tela Gestora (a tela aberta por uma aba informa a aba, para voltar para ela com o filtro dela)
export const ABA_GESTORA = {
  SOLICITACAO: "1",
  PREVISAO: "3",
};

/*
 * Retorna o valor para reaprovar o pagamento (valor a pagar + impostos, o mesmo valor da alçada e do grid da aba Solicitação),
 * a partir dos logs da aprovação (original e novo do pagamento): { valorAnterior, valor, valorAprovado }.
 * O valorAnterior só vem quando o valor foi alterado. Sem o log do pagamento, usa o pagamento atual.
 * O valorAprovado é o valor antes da reaprovação (o do log original), com ou sem alteração; sem o log, é o próprio valor.
 * @logs: logs da aprovação (solicitacao_aprovacao_log_pag)
 * @numPag: número do pagamento reaprovado
 * @pagamento: pagamento atual (valor_pagamento e valor_imposto)
 */
export function fn_getValorReaprovacaoPag(logs, numPag, pagamento) {
  //Valor a pagar + impostos
  const getValor = (pag) =>
    Number(pag.valor_pagamento || 0) + Number(pag.valor_imposto || 0);

  const logsPag = (logs || []).filter((log) => log.num_pag == numPag);
  const logOrig = logsPag.find(
    (log) => log.tipo == TIPO_LOG_APROVACAO.ORIGINAL,
  );
  const logNovo = logsPag.find((log) => log.tipo == TIPO_LOG_APROVACAO.NOVO);

  const pagValor = logNovo || pagamento;

  if (!pagValor) {
    return null;
  }

  const valor = getValor(pagValor);
  const isAlterado = logOrig != null && getValor(logOrig) != valor;

  return {
    valorAnterior: isAlterado ? getValor(logOrig) : null,
    valor: valor,
    valorAprovado: logOrig ? getValor(logOrig) : valor,
  };
}

/*
 * Retorna o nome do valor já aprovado do pagamento na reaprovação, conforme o tipo do valor da solicitação:
 * "Valor aprovado" (valor fixo) ou "Valor previsto" (valor estimado)
 */
export function fn_getLabelValorAprovadoReaprovacao(tipoValor) {
  return tipoValor == TIPO_VALOR.ESTIMADO ? "Valor previsto" : "Valor aprovado";
}

/*
 * Retorna o filtro que a aba da Gestora carrega ao voltar de uma tela aberta pela Gestora: { bodyState, pageState },
 * ou {} para carregar o filtro padrão.
 * A aba que abriu a tela usa o filtro que estava na tela ao abrir; a outra aba usa o filtro da última consulta dela.
 * @state: state da navegação (location.state)
 * @aba: aba da Gestora (ABA_GESTORA)
 */
export function fn_getFiltroVoltaAbaGestora(state, aba) {
  const abaOrigem = state?.abaGestora || ABA_GESTORA.SOLICITACAO;

  let filtro = null;
  if (abaOrigem == aba && state?.bodyState) {
    filtro = { bodyState: state.bodyState, pageState: state.pageState };
  } else {
    filtro = state?.filtrosGestora?.[aba];
  }

  if (!filtro?.bodyState) {
    return {};
  }

  //Cópia: a aba ajusta os campos do filtro ao carregar e não pode alterar o state da navegação
  return { bodyState: { ...filtro.bodyState }, pageState: filtro.pageState };
}

//Carrega as opções de área do usuário logado, verificando áreas liberadas para o perfil e áreas alçada
export async function fn_loadAcessoArea(sessaoUsuario) {
  //Carrega a alçada do usuário
  const idPerfil = fn_getUsuario(sessaoUsuario)?.perfil?.id;
  const alcada = await api_findUniqueAlcada(idPerfil);

  //Carrega as opções de área
  const opcoesAreaFull = await api_loadOpcoes(ROTA_LOAD.AREA, `situacao=A`);
  const areasPerfil = fn_getAreasUsuario(sessaoUsuario);

  const opcoesAreaAcesso = fn_getOpcoesAreasAcessoUsuario(
    opcoesAreaFull,
    alcada,
    areasPerfil,
  );

  return { opcoesAreaFull, opcoesAreaAcesso, alcada };
}

//Retorna as opções de área de acesso do usuário
export function fn_getOpcoesAreasAcessoUsuario(
  opcoesAreaFull,
  alcada,
  areasPerfil,
) {
  //a princípio tem acesso em todas as áreas
  let opcoesAreaAcesso = [...opcoesAreaFull];

  const areasAlcada = alcada
    ? alcada.areas.length > 0
      ? alcada.areas.map((item) => item.area)
      : opcoesAreaFull
    : [];

  //áreas de acesso do usuario = Áreas liberadas no perfil + áreas liberadas na alçada
  const areasAcesso = [...areasPerfil, ...areasAlcada];

  if (areasAcesso.length > 0) {
    //Pega os disntintos ids da área
    const idsAreasAcesso = new Set(
      areasAcesso.map((area) => area.id || area.value),
    );

    //Filtra as áreas
    opcoesAreaAcesso = opcoesAreaAcesso.filter((opcaoArea) =>
      idsAreasAcesso.has(opcaoArea.value),
    );
  }

  return opcoesAreaAcesso;
}

//Retorna as opções de tipo solicitação que o perfil do usuário pode acessar
export function fn_getOpcoesTipoSolicitacaoPerfil(
  sessaoUsuario,
  opcoesTipoSolicitacaoFull = [],
) {
  //Se não passar os tipos da solicitação, obtém
  if (opcoesTipoSolicitacaoFull.length == 0) {
    opcoesTipoSolicitacaoFull = fn_loadOpcoesTipoSolicitacao();
  }

  const tiposSolUsuario = fn_getTiposSolicitacaoUsuario(sessaoUsuario);

  //O default é acessar todos
  let opcoesTipoSolAcesso = opcoesTipoSolicitacaoFull;

  //Apenas se foi liberado algum tipo é que vai restringir o acesso
  if (tiposSolUsuario.length > 0) {
    opcoesTipoSolAcesso = opcoesTipoSolicitacaoFull.filter((tipo) =>
      tiposSolUsuario.includes(tipo.value),
    );
  }

  return opcoesTipoSolAcesso;
}

//Verifica se houve mudança da situação da solicitação
export function fn_mudouSituacaoSolicitacao(tipo, situacaoGrid, situacaoAtual) {
  if (situacaoGrid != situacaoAtual) {
    if (tipo == "solicitacao") {
      const descSituacaoGrid = fn_getSituacaoSolicitacao(situacaoGrid).label;
      const descSituacaoAtual = fn_getSituacaoSolicitacao(situacaoAtual).label;

      // showMensagemAviso(
      //   `Essa solicitação mudou da situação ${descSituacaoGrid} para ${descSituacaoAtual}. Realize a consulta novamente.`,
      // );
      showMensagemAviso(
        `Essa solicitação não está mais ${descSituacaoGrid}. Ela está  ${descSituacaoAtual}. Realize a consulta novamente.`,
      );
      return true; //mudou a situação
    } else if (tipo == "pagamento") {
      const descSituacaoGrid = fn_getSituacaoPagamento(situacaoGrid).label;
      const descSituacaoAtual = fn_getSituacaoPagamento(situacaoAtual).label;

      // showMensagemAviso(
      //   `Esse pagamento mudou da situação ${descSituacaoGrid} para ${descSituacaoAtual}. Realize a consulta novamente.`,
      // );
      showMensagemAviso(
        `Esse pagamento não está mais ${descSituacaoGrid}. Ele está ${descSituacaoAtual}. Realize a consulta novamente.`,
      );
      return true; //mudou a situação
    }
  }

  return false; //não mudou a situação
}

//Retorna os dintintos centros dos arrays
export function fn_getCentrosDistintos(centros1, centros2) {
  const centros = [...centros1, ...centros2];
  return [...new Map(centros.map((centro) => [centro.value, centro])).values()];
}

//Retorna os centros de custos do rateio, atualizados, adicionando os novos centros
//no caso de ter marcado a opção "TODOS" de determinado tipo de centro
export function fn_getCentrosSolicitacaoAtualizados(
  pagamento,
  centrosSolicitacao,
  centros,
) {
  //Obtém os centros de custos que foram salvos nos rateios da solicitação
  const map = new Map();

  const centrosLoja = centros.filter(
    (centro) => centro.tipo == TIPO_CENTRO_CUSTO.LOJA,
  );

  const centrosAdm = centros.filter(
    (centro) => centro.tipo == TIPO_CENTRO_CUSTO.ADMINISTRATIVO,
  );

  const centrosCD = centros.filter(
    (centro) => centro.tipo == TIPO_CENTRO_CUSTO.CENTRO_DISTRIBUICAO,
  );

  const centrosSemEmpresa = centros.filter(
    (centro) => centro.id_empresa == null,
  );

  //PENDENTE: REVER ESSA FUNÇÃO PQ ACHO QUE NO RATEIO AUTOMATICO, NÃO PODE MANTER O QUE ESTÁ GRAVADO
  //TERIA QUE REFAZER COMPLETO E NÃO SÓ ADICIONAR OS NOVOS.
  //Adiciona os centros do rateio
  pagamento.rateio.forEach((rateio) => {
    const centro = rateio.centro_custo;
    map.set(centro.value, centro);
  });

  let selectedCentros = Array.from(map.values());

  //Só faz o ajuste dos centros se o pagamento não estiver conferido
  if (pagamento.situacao != SITUACAO_PAGAMENTO.CONFERIDO) {
    //Verifica se foi selecionada alguma opção de TODOS na seleção dos centros para adicionar os novos
    for (const solCentro of centrosSolicitacao) {
      //Se selecionou TODOS centros loja de alguma empresa
      if (solCentro.id_empresa != null && solCentro.tipo == "T") {
        const centrosEmpresa = centrosLoja.filter(
          (centro) => centro.id_empresa == solCentro.id_empresa,
        );

        const centrosNovos = centrosEmpresa
          .filter(
            (centroEmp) =>
              !selectedCentros.some(
                (centro) => centro.value == centroEmp.value,
              ),
          )
          .map((centroEmp) => ({
            ...centroEmp,
            novo: true,
          }));

        selectedCentros = [...selectedCentros, ...centrosNovos];
      }

      //Se selecionou TODOS os centros sem empresa (Opção antiga que será eliminada depois)
      else if (solCentro.id_empresa == null && solCentro.tipo == "A") {
        const centrosNovos = centrosSemEmpresa
          .filter(
            (centro) =>
              !selectedCentros.some(
                (centroSelecionado) => centroSelecionado.value == centro.value,
              ),
          )
          .map((centro) => ({
            ...centro,
            novo: true,
          }));

        selectedCentros = [...selectedCentros, ...centrosNovos];
      }

      //Se selecionou TODOS os centros ADM
      else if (solCentro.id_empresa == null && solCentro.tipo == "TA") {
        const centrosNovos = centrosAdm
          .filter(
            (centroAdm) =>
              !selectedCentros.some(
                (centro) => centro.value == centroAdm.value,
              ),
          )
          .map((centroAdm) => ({
            ...centroAdm,
            novo: true,
          }));

        selectedCentros = [...selectedCentros, ...centrosNovos];
      }

      //Se selecionou TODOS os centros CD
      else if (solCentro.id_empresa == null && solCentro.tipo == "TC") {
        const centrosNovos = centrosCD
          .filter(
            (centroCD) =>
              !selectedCentros.some((centro) => centro.value == centroCD.value),
          )
          .map((centroCD) => ({
            ...centroCD,
            novo: true,
          }));

        selectedCentros = [...selectedCentros, ...centrosNovos];
      }
    }
  }

  return selectedCentros;
}

/*
 * Retorna as conferências de todos os pagamentos da solicitação em uma lista só,
 * ordenada pelo número do pagamento e pela data da conferência.
 * Cada conferência recebe o "num_pag", o "tipo_integracao" e a "integracao_retirada" do seu pagamento
 * (a integração é gravada no pagamento, e não na conferência).
 * @pagamentos: Pagamentos da solicitação
 * @conferencias: Conferências desses pagamentos
 */
export function fn_montarConferenciasPagamentos(
  pagamentos = [],
  conferencias = [],
) {
  return conferencias
    .map((conf) => {
      const pagamento = pagamentos.find(
        (pag) => pag.id == conf.id_solicitacao_pag,
      );

      return {
        ...conf,
        num_pag: pagamento?.num_pag,
        tipo_integracao: pagamento?.tipo_integracao,
        integracao_retirada: pagamento?.integracao_retirada,
      };
    })
    .sort(
      (a, b) =>
        a.num_pag - b.num_pag ||
        new Date(a.data) - new Date(b.data) ||
        a.id - b.id,
    );
}

//Retorna a situação da solicitação para apresentar na tela
export function fn_getJsxSituacaoSolicitacao(situacao) {
  const objSituacao = fn_getSituacaoSolicitacao(situacao);

  if (objSituacao) {
    return <span className={objSituacao.className}>{objSituacao.label}</span>;
  }
}

//Retorna a situação do pagamento para apresentar na tela
export function fn_getJsxSituacaoPagamento(situacao) {
  const objSituacao = fn_getSituacaoPagamento(situacao);

  if (objSituacao) {
    return <span className={objSituacao.className}>{objSituacao.label}</span>;
  }
}

//Retorna a situação da conferência para apresentar na tela
export function fn_getJsxSituacaoConferencia(situacao) {
  const objSituacao = fn_getSituacaoConferencia(situacao);

  if (objSituacao) {
    return <span className={objSituacao.className}>{objSituacao.label}</span>;
  }
}

//Retorna a situação do reajuste para apresentar na tela
export function fn_getJsxSituacaoReajuste(situacao) {
  const objSituacao = fn_getSituacaoReajuste(situacao);

  if (objSituacao) {
    return <span className={objSituacao.className}>{objSituacao.label}</span>;
  } else {
    return <span className="text-pendente">Pendente</span>;
  }
}

//Retorna o tipo da solicitação para apresentar na tela
export function fn_getJsxCampoTipoSolicitacao(tipo, showIcone = false) {
  const tipoSolicitacao = fn_getTipoSolicitacao(tipo);

  return (
    <span className="fw-bold text-nowrap">
      {showIcone ? (
        <span className="me-2">{fn_getIconeTipoSolicitacao(tipo)}</span>
      ) : (
        ""
      )}
      <span>{tipoSolicitacao.label.toUpperCase()}</span>
    </span>
  );
}

//Retorna o jsx para apresentar o tipo do pagamento na tela
export function fn_getJsxTipoPagamento(tipoPagamento) {
  const tipoPag = fn_getTipoPagamento(tipoPagamento);

  return (
    <span className="ms-1 text-nowrap">
      {tipoPag.value == TIPO_PAGAMENTO.UNICO
        ? ICONES_SIIG.PAG_UNICO
        : tipoPag.value == TIPO_PAGAMENTO.PARCELADO
          ? ICONES_SIIG.PAG_PARCELADO
          : tipoPag.value == TIPO_PAGAMENTO.RECORRENTE
            ? ICONES_SIIG.PAG_RECORRENTE
            : ""}

      <span className="ms-1">{tipoPag.label}</span>
    </span>
  );
}

//Retorna só o ícone do tipo pagamento (único, parcelado ou recorrente), com o nome do tipo no tooltip (ex: coluna de grid)
export function fn_getJsxIconeTipoPagamento(tipoPagamento) {
  const tipoPag = fn_getTipoPagamento(tipoPagamento);

  const icone =
    tipoPag?.value == TIPO_PAGAMENTO.UNICO
      ? ICONES_SIIG.PAG_UNICO
      : tipoPag?.value == TIPO_PAGAMENTO.PARCELADO
        ? ICONES_SIIG.PAG_PARCELADO
        : tipoPag?.value == TIPO_PAGAMENTO.RECORRENTE
          ? ICONES_SIIG.PAG_RECORRENTE
          : null;

  if (!icone) {
    return null;
  }

  return (
    <TooltipItem text={tipoPag.label} position="top">
      <span aria-label={tipoPag.label}>{icone}</span>
    </TooltipItem>
  );
}

//Retorna a classe do css para a cor da data de vencimento.
//Se já venceu: vermelho; Se é vecimento do dia: negrito
export function fn_getClassColorDataVencimento(
  dataVencimento, //formato DD/MM/YYYY
  situacao,
  datasFeriados,
) {
  const dataAtual = fn_getDataAtual();
  const dataInicioPosicaoDia = fn_getDataInicioPosicaoDia(
    dataAtual,
    datasFeriados,
  );

  if (
    [
      //Pagamento ou solicitação com uma dessas situações
      SITUACAO_PAGAMENTO.PENDENTE,
      SITUACAO_PAGAMENTO.PENDENTE_REAPROVACAO,
      SITUACAO_PAGAMENTO.REJEITADA_REAPROVACAO,
      SITUACAO_PAGAMENTO.ERRO,
      SITUACAO_PAGAMENTO.CONTROLE,
    ].includes(situacao)
  ) {
    //Se a data de vencimento for anterior à data inicio da posição dia, está vencido
    if (fn_dataIsBefore(dataVencimento, dataInicioPosicaoDia)) {
      return "text-danger";
    }
    //Se a data de vencimento não for posterior à data atual
    else if (!fn_dataIsAfter(dataVencimento, dataAtual)) {
      return "fw-bold";
    }
  }

  return "";
}

//Retorna se a data de vencimento do pagamento está vencida
export function fn_getIsVencido(
  dataVencimento, //formato DD/MM/YYYY
  datasFeriados,
) {
  const dataInicioPosicaoDia = fn_getDataInicioPosicaoDia(
    fn_getDataAtual(),
    datasFeriados,
  );

  if (fn_dataIsBefore(dataVencimento, dataInicioPosicaoDia)) {
    return true;
  } else {
    return false;
  }
}

//Calcula a data da emissão e de vencimento conforme dados informados para gerar os pagamentos
export function fn_calcularDataEmissaoVencimento(
  numPag,
  infGeracaoPag,
  opRealizada = false,
) {
  const tipoVencimento = infGeracaoPag.tipo_vencimento.value;
  const diaVencimento = infGeracaoPag.dia_vencimento;
  const diasIntervalo = Number(infGeracaoPag.intervalo_vencimento || 0);
  const dataPrimVencimento = infGeracaoPag.data_prim_vencimento;

  let dataVencimento = dataPrimVencimento;
  if (numPag > 1) {
    //Se o tipo do vencimento for por intervalo de dias
    if (tipoVencimento == TIPO_VENCIMENTO.INTERVALO_DIAS) {
      dataVencimento = fn_addDay(
        dataPrimVencimento,
        diasIntervalo * (numPag - 1),
      );
    }
    //Se for dia fixo
    else {
      let dia = String(diaVencimento);
      const mesAno = fn_addMonth(
        fn_getMesAnoData(dataPrimVencimento),
        numPag - 1,
      );
      const ultimoDiaMes = fn_getUltimoDiaMes(mesAno);

      if (Number(diaVencimento) > Number(ultimoDiaMes)) {
        dia = String(ultimoDiaMes);
      }

      dataVencimento = dia.padStart(2, "0") + "/" + mesAno;
    }
  }

  const diasAntesEmissao = infGeracaoPag.dias_ant_emissao || 0;
  let dataEmissao = fn_addDay(dataVencimento, -diasAntesEmissao);

  //## RETIRADA A REGRA A PEDIDO DA ANDREA - 01/09/26
  // //No primeiro pag, se a operação já foi realizada e a emissão ficou depois da data atual
  // if (
  //   opRealizada &&
  //   numPag == 1 &&
  //   fn_dataIsAfter(dataEmissao, fn_getDataAtual())
  // ) {
  //   //Deixa a emissão com a data atual para ficar como realizada
  //   dataEmissao = fn_getDataAtual();
  // }

  return { dataEmissao, dataVencimento };
}

//Calcula o mês/ano ref. do pagamento conforme a emissão e os dados informados para gerar os pagamentos
export function fn_calcularMesAnoRefPagamento(dataEmissao, infGeracaoPag) {
  const qtdeMeses = infGeracaoPag.qtde_meses_mes_ano_ref || 1;

  return infGeracaoPag.tipo_mes_ano_ref == "AE" //aterior à emissão
    ? fn_addMonth(fn_getMesAnoData(dataEmissao), -qtdeMeses)
    : fn_getMesAnoData(dataEmissao);
}

//Pagamento único com operação já realizada: a emissão é informada na geração dos pagamentos (no lugar dos dias antes do vencimento).
//Retorna a mensagem de erro da emissão ou null se estiver correta.
//O vencimento só é comparado se for uma data válida (a validação dele é feita à parte).
export function fn_getMsgErroEmissaoPagUnico(dataEmissao, dataVencimento) {
  if (!fn_validarData(dataEmissao)) {
    return getMensagemErro(MENSAGENS_ERRO.DATA_INVALIDA);
  }

  if (
    fn_validarData(dataVencimento) &&
    fn_dataIsAfter(dataEmissao, dataVencimento)
  ) {
    return getMensagemErro(
      MENSAGENS_ERRO.DATA_X_ANTERIOR_OU_IGUAL_DATA_Y,
      "emissão",
      "vencimento",
    );
  }

  return null;
}

//Pagamento único com operação já realizada: retorna os dias da emissão antes do vencimento, que é o que a solicitação grava
//e o que a geração dos pagamentos usa (fn_calcularDataEmissaoVencimento) para chegar na emissão informada
export function fn_getDiasAntEmissaoPagUnico(dataEmissao, dataVencimento) {
  return fn_getDifDiasDatas(dataEmissao, dataVencimento);
}

//Retorna o texto com os números dos pagamentos, para as mensagens das telas.
//Os pagamentos seguidos aparecem como faixa e os grupos são separados por ";" (pagamento pulado quebra a faixa).
//Ex: [4] = "pagamento 4"; [4, 5] = "pagamentos 4 a 5"; [3, 5, 6, ..., 12] = "pagamentos 3; 5 a 12"
//@abreviado: true para "pag. 4" / "pag. 3; 5 a 12", nas mensagens com pouco espaço
export function fn_getDescNumsPagamentos(numsPag, abreviado = false) {
  const nums = [...new Set(numsPag.map(Number))].sort((a, b) => a - b);

  //Agrupa os números seguidos
  const grupos = [];
  for (const num of nums) {
    const ultimoGrupo = grupos.at(-1);

    if (ultimoGrupo && num == ultimoGrupo.at(-1) + 1) {
      ultimoGrupo.push(num);
    } else {
      grupos.push([num]);
    }
  }

  const descNums = grupos
    .map((grupo) =>
      grupo.length > 1 ? `${grupo[0]} a ${grupo.at(-1)}` : String(grupo[0]),
    )
    .join("; ");

  const descPagamento = abreviado
    ? "pag."
    : nums.length == 1
      ? "pagamento"
      : "pagamentos";

  return `${descPagamento} ${descNums}`;
}

//### MENSAGENS DAS ALTERAÇÕES APLICADAS NOS PAGAMENTOS ###
//@inf: { titulo, numsAlterar, numsNaoAlterar, motivoNaoAlterar (plural), motivoNaoAlterarSingular }
//      numsAlterar: pagamentos que são alterados; numsNaoAlterar: pagamentos que ficam sem alterar pelo motivo

//Retorna o motivo dos pagamentos que não são alterados, no singular quando é um pagamento só
export function fn_getMotivoNaoAlterarPagamentos(inf) {
  return inf.numsNaoAlterar.length == 1 && inf.motivoNaoAlterarSingular
    ? inf.motivoNaoAlterarSingular
    : inf.motivoNaoAlterar;
}

//Retorna a linha da confirmação (HTML), com os pagamentos abreviados para não quebrar linha.
//Ex: "<b>Tipo documento:</b> pag. 4 a 5" + "Não altera pag. 1 a 3 (conferidos, cancelados ou com posse)"
//Cada informação fica em um bloco: a tela junta os blocos alinhados à esquerda (fn_getHtmlConfirmacaoAlteracoesPagamentos)
export function fn_getConfirmacaoAlteracaoPagamentos(inf) {
  const descAlterar =
    inf.numsAlterar.length > 0
      ? fn_getDescNumsPagamentos(inf.numsAlterar, true)
      : "nenhum pagamento";

  const descNaoAlterar =
    inf.numsNaoAlterar.length > 0
      ? `<div class="texto-menor">Não altera ${fn_getDescNumsPagamentos(inf.numsNaoAlterar, true)} (${fn_getMotivoNaoAlterarPagamentos(inf)})</div>`
      : "";

  return `<div class="mb-2"><b>${inf.titulo}:</b> ${descAlterar}${descNaoAlterar}</div>`;
}

//Retorna o texto (HTML) da confirmação com a pergunta e as informações alinhadas à esquerda, uma embaixo da outra
export function fn_getHtmlConfirmacaoAlteracoesPagamentos(pergunta, infs) {
  return `${pergunta}<div class="text-start mt-3">${infs
    .map((inf) => fn_getConfirmacaoAlteracaoPagamentos(inf))
    .join("")}</div>`;
}

//Retorna a linha do resumo da mensagem de sucesso, depois de aplicar. Ex: "Tipo documento: alterado pag. 3; 5 a 12"
//Informa só os pagamentos alterados (os que não são alterados já foram informados na confirmação), abreviado
//para a mensagem ficar curta, pois ela some sozinha depois de alguns segundos
export function fn_getResumoAlteracaoPagamentos(inf) {
  const descAlterar =
    inf.numsAlterar.length > 0
      ? `alterado ${fn_getDescNumsPagamentos(inf.numsAlterar, true)}`
      : "nenhum pagamento alterado";

  return `${inf.titulo}: ${descAlterar}`;
}

//Correção da solicitação: retorna o número do primeiro pagamento a partir do qual a emissão e o vencimento
//podem ser gerados em sequência, que é o pagamento seguinte ao último conferido, cancelado ou com posse.
//Retorna null se não houver pagamento depois dele.
export function fn_getNumPagInicioAplicarDatasCorrecao(pagamentos) {
  const numPagsBloqueados = pagamentos
    .filter((pag) => fn_isPagamentoBloqueadoValoresCorrecao(pag))
    .map((pag) => Number(pag.num_pag));

  const ultimoBloqueado =
    numPagsBloqueados.length > 0 ? Math.max(...numPagsBloqueados) : 0;

  const numPagsLiberados = pagamentos
    .map((pag) => Number(pag.num_pag))
    .filter((numPag) => numPag > ultimoBloqueado);

  return numPagsLiberados.length > 0 ? Math.min(...numPagsLiberados) : null;
}

//Informações do pagamento 1 que, no parcelado, ficam gravadas iguais em todas as parcelas
//(como no cadastro da solicitação e na posse do documento)
const CAMPOS_PARCELADO_IGUAIS_PAG1 = [
  "tipo_documento",
  "id_tipo_documento",
  "tipo_documento_novo",
  "id_forma_pagamento",
  "forma_pagamento",
  "tipo_rateio",
  "forma_rateio",
];

//Correção da solicitação parcelada: retorna os pagamentos com as parcelas 2 em diante com as informações do pagamento 1
//que ficam gravadas iguais em todas (tipo documento, forma pagamento, tipo e forma do rateio).
//Na correção essas informações são alteradas só no pagamento 1.
//OBS: O que não pode ser alterado na parcela (conferida, cancelada ou com posse) o servidor não grava.
export function fn_getPagamentosParceladoIguaisPag1Correcao(pagamentos) {
  const pag1 = pagamentos.find((pag) => pag.num_pag == 1);

  if (!pag1) {
    return pagamentos;
  }

  const infPag1 = Object.fromEntries(
    CAMPOS_PARCELADO_IGUAIS_PAG1.map((campo) => [campo, pag1[campo]]),
  );

  return pagamentos.map((pag) =>
    pag.num_pag == 1 ? pag : { ...pag, ...infPag1 },
  );
}

//Correção da solicitação: calcula o mês/ano ref. e, se informado infDatas, a emissão e o vencimento
//dos pagamentos a partir do pagamento inicial. Os pagamentos anteriores não entram no retorno.
//O pagamento inicial recebe o vencimento informado e os seguintes são calculados pela distância até ele,
//com a mesma regra do cadastro da solicitação (fn_calcularDataEmissaoVencimento).
//@infDatas: { tipo_vencimento, dia_intervalo_vencimento, dias_ant_emissao, data_vencimento_inicio } ou null (não altera as datas)
//@tipoMesAnoRef: "E" (mês/ano da emissão), "AE" (qtde de meses anterior à emissão) ou vazio (não altera o mês/ano ref.).
//O mês/ano ref. do pagamento cancelado não é alterado
//@returns Objeto com a chave num_pag e os campos alterados do pagamento
export function fn_calcularDatasPagamentosCorrecao({
  pagamentos,
  numPagInicio,
  infDatas = null,
  tipoMesAnoRef = null,
  qtdeMesesMesAnoRef = 1,
}) {
  const alteracoes = {};

  const infGeracaoPag = infDatas
    ? {
        tipo_vencimento: { value: infDatas.tipo_vencimento },
        dia_vencimento: infDatas.dia_intervalo_vencimento,
        intervalo_vencimento: infDatas.dia_intervalo_vencimento,
        dias_ant_emissao: Number(infDatas.dias_ant_emissao || 0),
        data_prim_vencimento: infDatas.data_vencimento_inicio,
      }
    : null;

  for (const pag of pagamentos) {
    const numPag = Number(pag.num_pag);

    if (numPag < Number(numPagInicio)) continue;

    const alteracao = {};
    let dataEmissao = pag.data_emissao;

    if (infGeracaoPag) {
      //O pagamento inicial faz o papel do primeiro pagamento do cadastro
      const datas = fn_calcularDataEmissaoVencimento(
        numPag - Number(numPagInicio) + 1,
        infGeracaoPag,
      );

      dataEmissao = datas.dataEmissao;
      alteracao.data_emissao = datas.dataEmissao;
      alteracao.data_vencimento = datas.dataVencimento;
    }

    if (tipoMesAnoRef && fn_permiteAlterarMesAnoRefCorrecao(pag.situacao)) {
      alteracao.mes_ano_ref = fn_calcularMesAnoRefPagamento(dataEmissao, {
        tipo_mes_ano_ref: tipoMesAnoRef,
        qtde_meses_mes_ano_ref: qtdeMesesMesAnoRef,
      });
    }

    alteracoes[numPag] = alteracao;
  }

  return alteracoes;
}

//Verifica se a quantidade de arquivos dos pagamentos (documento, forma de pagamento ou anexo complementar) selecionados para envio
//é maior que a permitida em uma gravação. Só conta o arquivo selecionado agora; o que já foi gravado não é enviado de novo
export function fn_excedeQtdeArquivosPagEnvio(
  pagamentos,
  qtdeMax = MAX_QTDE_ARQUIVOS_PAG_ENVIO,
) {
  const qtdeArquivosDoc = pagamentos.filter(
    (pag) => pag.arquivo_documento,
  ).length;
  const qtdeArquivosFormaPag = pagamentos.filter(
    (pag) => pag.arquivo_forma_pag,
  ).length;
  const qtdeArquivosComplementar = pagamentos.filter(
    (pag) => pag.arquivo_complementar,
  ).length;

  return (
    qtdeArquivosDoc > qtdeMax ||
    qtdeArquivosFormaPag > qtdeMax ||
    qtdeArquivosComplementar > qtdeMax
  );
}

//Verifica se o tipo documento e/ou a forma pagamento do pagamento está diferente do informado para gerar os pagamentos
export function fn_pagamentoDivergeInfPosse(
  pagamento,
  tipoDocumento,
  formaPagamento,
) {
  return (
    pagamento.tipo_documento?.value != tipoDocumento?.value ||
    pagamento.forma_pagamento?.value != formaPagamento?.value
  );
}

/*
 * Retorna o campo da informação adicional que a posse apresenta para a forma pagamento (campos_forma_pag.jsx),
 * ou null quando a posse não tem o campo. Na receita a posse só tem a conta bancária.
 */
export function fn_getCampoInfAdicionalPosse(formaPagamento, possuiReceita) {
  if (possuiReceita) return null;

  switch (formaPagamento?.sigla) {
    case SIGLA_FORMA_PAGAMENTO.BOLETO:
    case SIGLA_FORMA_PAGAMENTO.BOLETO_DDA:
      return "NUMERO_BOLETO";
    case SIGLA_FORMA_PAGAMENTO.CHEQUE:
      return "NUMERO_CHEQUE";
    case SIGLA_FORMA_PAGAMENTO.PIX:
      return "CHAVE_PIX";
    case SIGLA_FORMA_PAGAMENTO.DEBITO_CONTA:
    case SIGLA_FORMA_PAGAMENTO.TED:
      return null;
    default:
      return "INF_ADICIONAL";
  }
}

/*
 * Retorna de quem são as contas bancárias que a posse apresenta para a forma pagamento (campos_forma_pag.jsx):
 * "EMPRESA", "FORNECEDOR", ou null quando a posse não tem a conta bancária.
 * Na receita: conta da empresa. Na despesa: conta da empresa no cheque e no débito em conta e conta do fornecedor na TED.
 */
export function fn_getPessoaContaBancariaPosse(formaPagamento, possuiReceita) {
  if (possuiReceita) return "EMPRESA";

  switch (formaPagamento?.sigla) {
    case SIGLA_FORMA_PAGAMENTO.CHEQUE:
    case SIGLA_FORMA_PAGAMENTO.DEBITO_CONTA:
      return "EMPRESA";
    case SIGLA_FORMA_PAGAMENTO.TED:
      return "FORNECEDOR";
    default:
      return null;
  }
}

/*
 * Indica se a conta bancária está na lista de contas da posse para a forma pagamento (campos_forma_pag.jsx):
 * no cheque, só as contas que permitem cheque; no débito em conta, só as que permitem débito; nas demais, todas.
 * @contaBancaria: conta do pagamento, com os indicadores permite_cheque e permite_debito
 */
export function fn_isContaBancariaListaFormaPag(contaBancaria, formaPagamento) {
  switch (formaPagamento?.sigla) {
    case SIGLA_FORMA_PAGAMENTO.CHEQUE:
      return contaBancaria?.permite_cheque == "S";
    case SIGLA_FORMA_PAGAMENTO.DEBITO_CONTA:
      return contaBancaria?.permite_debito == "S";
    default:
      return true;
  }
}

/*
 * Atualiza o tipo documento e a forma pagamento do pagamento com a posse marcada (realizada ou pendente) sem desfazer a posse:
 * limpa só o que não serve mais e mantém os arquivos (inclusive o anexado pela leitura do documento por IA).
 * - Tipo documento alterado: limpa o número do documento só se ele não couber no tamanho do número do tipo novo.
 * - Forma pagamento alterada: limpa a informação adicional e a conta bancária, a não ser que a posse da forma nova
 *   tenha o mesmo campo (ex: "Informação adicional" nas duas formas; conta do fornecedor nas duas).
 * A posse fica sempre pendente, para o usuário revisar na tela da posse (o Salvar recusa a posse pendente).
 * Spec: documentos/specs/2026-10-27-gerar-pagamentos-revisar-posse.md
 * @possuiReceita: a solicitação tem conta de receita (a posse exige a conta bancária)
 */
export function fn_revisarPossePagamento(
  pagamento,
  tipoDocumento,
  formaPagamento,
  possuiReceita,
) {
  const alterouTipoDocumento =
    pagamento.tipo_documento?.value != tipoDocumento?.value;
  const alterouFormaPagamento =
    pagamento.forma_pagamento?.value != formaPagamento?.value;

  //Número: limpa só se não couber no tamanho do número do tipo novo (o mesmo limite do campo na posse)
  const numeroNaoCabe =
    alterouTipoDocumento &&
    String(pagamento.num_documento ?? "").length >
      (tipoDocumento?.numero_tamanho || 44);

  //Informação adicional e conta bancária: limpa se a posse da forma nova não tiver o mesmo campo
  const campoInfAnterior = fn_getCampoInfAdicionalPosse(
    pagamento.forma_pagamento,
    possuiReceita,
  );
  const campoInfAlterado =
    alterouFormaPagamento &&
    (campoInfAnterior == null ||
      campoInfAnterior !=
        fn_getCampoInfAdicionalPosse(formaPagamento, possuiReceita));

  //Conta: continua só se a posse da forma nova tiver conta da mesma pessoa (empresa ou fornecedor)
  //e a conta estiver na lista da forma nova (ex: no cheque, só a conta que permite cheque)
  const pessoaContaAnterior = fn_getPessoaContaBancariaPosse(
    pagamento.forma_pagamento,
    possuiReceita,
  );
  const campoContaAlterado =
    alterouFormaPagamento &&
    (pessoaContaAnterior == null ||
      pessoaContaAnterior !=
        fn_getPessoaContaBancariaPosse(formaPagamento, possuiReceita) ||
      !fn_isContaBancariaListaFormaPag(
        pagamento.conta_bancaria,
        formaPagamento,
      ));

  const pag = {
    ...pagamento,
    tipo_documento: tipoDocumento,
    forma_pagamento: formaPagamento,
    id_forma_pagamento: formaPagamento?.value || null,
    ...(numeroNaoCabe ? { num_documento: null } : {}),
    ...(campoInfAlterado ? { inf_pagamento: null } : {}),
    ...(campoContaAlterado
      ? { conta_bancaria: null, id_conta_bancaria: null }
      : {}),
  };

  //A posse fica sempre pendente, para obrigar o usuário a entrar na tela da posse e revisar os dados
  //(ex: o número que foi limpo), mesmo que nada obrigatório tenha ficado faltando
  return {
    ...pag,
    situacao_documento: "P",
    id_usuario_posse: null,
  };
}

//Gera novamente a lista de pagamentos mantendo os valores, o rateio e a posse já informados.
//Refaz o mês/ano ref., a emissão e o vencimento. Pagamento que ainda não existia é criado pelo gerarPagamentoNovo.
//No pagamento com tipo documento e/ou forma pagamento diferente do informado para gerar, atualiza essas inf.:
//com a posse marcada, revisa a posse sem desfazer (fn_revisarPossePagamento); sem a posse, só atualiza.
export function fn_regerarPagamentosMantendoValores({
  pagamentosAtuais,
  infGeracaoPag,
  showDocPosse,
  isParcelado,
  selectedContas,
  selectedCentros,
  infsRateioAutomatico = {}, //inf. do rateio automático por num_pag, conforme a nova emissão
  gerarPagamentoNovo, //função (numPag) que retorna o pagamento novo
  getValoresRateioTipo, //função (pagamento, novosPagamentos) que retorna os valores por tipo a ratear
}) {
  const qtdePag = Number(infGeracaoPag.qtde_pagamento);

  const novosPagamentos = [];
  const numPagsMantidos = [];

  //1 - Refaz a lista dos pagamentos
  for (let numPag = 1; numPag <= qtdePag; numPag++) {
    const pagAtual = pagamentosAtuais.find((item) => item.num_pag == numPag);

    //Se o pagamento não existia, gera um novo
    if (!pagAtual) {
      novosPagamentos.push(gerarPagamentoNovo(numPag));
      continue;
    }

    const { dataEmissao, dataVencimento } = fn_calcularDataEmissaoVencimento(
      numPag,
      infGeracaoPag,
      showDocPosse,
    );

    let pag = {
      ...pagAtual,
      mes_ano_ref: fn_calcularMesAnoRefPagamento(dataEmissao, infGeracaoPag),
      data_emissao: dataEmissao,
      data_vencimento: dataVencimento,
    };

    if (infsRateioAutomatico[numPag]) {
      pag.inf_rateio_automatico = infsRateioAutomatico[numPag];
    }

    //Se o tipo documento e/ou a forma pagamento do pagamento estiver diferente do informado para gerar
    //(alterou no formulário ou na posse), atualiza essas inf.: com a posse marcada, a posse é revisada (sem desfazer)
    const divergeInfPosse = fn_pagamentoDivergeInfPosse(
      pagAtual,
      infGeracaoPag.tipo_documento,
      infGeracaoPag.forma_pagamento,
    );

    if (divergeInfPosse && pagAtual.doc_posse == "S") {
      pag = fn_revisarPossePagamento(
        pag,
        infGeracaoPag.tipo_documento,
        infGeracaoPag.forma_pagamento,
        fn_possuiAlgumaContaReceita(selectedContas),
      );
    } else if (divergeInfPosse) {
      pag = {
        ...pag,
        tipo_documento: infGeracaoPag.tipo_documento,
        forma_pagamento: infGeracaoPag.forma_pagamento,
        id_forma_pagamento: infGeracaoPag.forma_pagamento?.value || null,
        doc_posse: "N",
        id_usuario_posse: null,
        situacao_documento: "P",
        num_documento: null,
        conta_bancaria: null,
        id_conta_bancaria: null,
        inf_pagamento: null,
        arquivo_documento: null,
        arquivo_forma_pag: null,
        arquivo_complementar: null,
      };
    }

    numPagsMantidos.push(numPag);
    novosPagamentos.push(pag);
  }

  //2 - Atualiza o rateio dos pagamentos mantidos
  const valorTotalDoc = fn_calcularTotal(novosPagamentos, "valor_documento");
  const existePagZero = novosPagamentos.some(
    (item) => Number(item.valor_documento || 0) == 0,
  );

  for (const pag of novosPagamentos) {
    //Pagamento novo já foi gerado com a situação do rateio
    if (!numPagsMantidos.includes(pag.num_pag)) continue;

    //Quando é parcelado o rateio fica no pagamento 1
    if (isParcelado && pag.num_pag != 1) continue;

    const valorRateioDoc = isParcelado
      ? valorTotalDoc
      : Number(pag.valor_documento || 0);
    const valoresRateioPorTipo = getValoresRateioTipo(pag, novosPagamentos);

    //Só recalcula o rateio que depende do que pode ter mudado:
    //automático (mês/ano da emissão) ou parcelado (valor total das parcelas)
    if (
      (pag.tipo_rateio == TIPO_RATEIO.AUTOMATICO || isParcelado) &&
      (!isParcelado || !existePagZero)
    ) {
      pag.rateio = fn_getRateioPagamento(
        pag,
        valorRateioDoc,
        valoresRateioPorTipo,
        selectedContas,
        selectedCentros,
      );
    }

    pag.situacao_rateio = fn_getSituacaoRateioPagamento(
      pag,
      valorRateioDoc,
      valoresRateioPorTipo,
      selectedCentros,
    );
  }

  return novosPagamentos;
}

//Retorna as opções de tipo apropriação conforme o tipo das contas da solicitação
//(mesma regra do cadastro da solicitação: só receita, só pagamento ou todas as opções)
export function fn_getOpcoesTipoApropriacaoContas(contas) {
  return fn_loadOpcoesTipoApropriacao({
    tipo_solicitacao: fn_possuiSomenteContaReceita(contas)
      ? TIPO_SOLICITACAO.RECEITA
      : fn_possuiSomenteContaPagamento(contas)
        ? TIPO_SOLICITACAO.PAGAMENTO
        : null, //todas as opções
  });
}

//Calcula os totais de cada valor do pagamento
export function fn_calcularTotaisPagamentos(pagamentos) {
  const totais = fn_getTotaisCamposResult(pagamentos, [
    "valor_documento",
    "valor_desconto",
    "valor_devolucao",
    "valor_despesa",
    "valor_juros",
    "valor_multa",
    "valor_imposto",
    "valor_pagamento",
  ]);
  return totais;
}

//Verifica se a alteração do tipo documento do pagamento exige reaprovação:
//somente quando muda de um tipo que contabiliza para um tipo que não contabiliza
//OBS: Se o tipo documento original não for encontrado, considera que contabiliza
export function fn_isAlteracaoTipoDocExigeReaprovacao(
  tipoDocOrig,
  tipoDocNovo,
) {
  if (!tipoDocNovo) {
    return false;
  }

  return tipoDocOrig?.contabiliza != "N" && tipoDocNovo.contabiliza == "N";
}

//### REAPROVAÇÃO DO PAGAMENTO NA POSSE DO DOCUMENTO ###
//As funções abaixo comparam os valores da tela da posse com os da última aprovação do pagamento (pagOrig).
//@limites: { limite_valor, limite_vencimento } (parametrizações do cliente)

//Verifica se a alteração do tipo documento do pagamento exige reaprovação, comparando com o tipo documento aprovado
export function fn_isAlteracaoTipoDocPagExigeReaprovacao(
  pagOrig,
  tipoDocNovo,
  tiposDocumento,
) {
  //if (selectedTipoDoc.value != pagOrig.tipo_documento) {
  if (tipoDocNovo.id != pagOrig.id_tipo_documento) {
    //Só exige reaprovação se mudar de um tipo que contabiliza para um tipo que não contabiliza
    const tipoDocOrig = tiposDocumento.find(
      (tp) => tp.id == pagOrig.id_tipo_documento,
    );

    return fn_isAlteracaoTipoDocExigeReaprovacao(tipoDocOrig, tipoDocNovo);
  }

  return false;
}

//Verifica se a alteração da data de vencimento exige reaprovação: vencimento antecipado mais dias que o limite
export function fn_isAlteracaoVencimentoExigeReaprovacao(
  pagOrig,
  pagNovo,
  limiteVencimento,
) {
  const dataVencAtual = pagOrig.data_vencimento;
  const dataVencNovo = pagNovo.data_vencimento;

  if (fn_dataIsBefore(dataVencNovo, dataVencAtual)) {
    const difDias = fn_getDifDiasDatas(dataVencNovo, dataVencAtual);

    //Se a data for anterior a qtde de dias limite
    if (difDias > limiteVencimento) {
      return true;
    }
  }

  return false;
}

//Verifica se a alteração de um valor exige reaprovação: valor maior, com o aumento acima do limite
export function fn_isAlteracaoValorExigeReaprovacao(
  valorOrig,
  valorNovo,
  limiteValor,
) {
  const difValor = valorNovo - valorOrig;

  if (valorNovo > valorOrig && difValor > limiteValor) {
    return true;
  }

  return false;
}

//Verifica se os valores do pagamento ficaram maiores que os aprovados (na solicitação de valor fixo, pede o motivo
//da alteração e gera reaprovação). No parcelado são informados os totais das parcelas
export function fn_isValorMaiorQueAprovado(valoresOrig, valoresNovo) {
  const valorDocNovo = Number(valoresNovo.valor_documento);
  const valorDocOrig = Number(valoresOrig.valor_documento);

  const valorPagNovo = Number(valoresNovo.valor_pagamento);
  const valorPagOrig = Number(valoresOrig.valor_pagamento);

  return valorDocNovo > valorDocOrig || valorPagNovo > valorPagOrig;
}

//Verifica quais alterações do pagamento geram reaprovação
//@isParcelado: no parcelado o tipo documento e os valores são conferidos na parcela 1, pelo total das parcelas
//              (totaisOrig e totais); o vencimento é conferido em todas as parcelas
//@returns { gerarLog, camposLog: { tipo_documento, data_vencimento, valor_documento, valor_pagamento } }
export function fn_verificarGeracaoLogReaprovacao({
  pagOrig,
  pagNovo,
  isParcelado,
  totaisOrig,
  totais,
  tipoDocNovo,
  tiposDocumento,
  limites,
}) {
  let gerarLog = false;

  let camposLog = {
    // gerar_log: false,
    tipo_documento: false,
    data_vencimento: false,
    valor_documento: false,
    valor_pagamento: false,
  };

  if (!fn_isEmpty(pagOrig)) {
    //Se for pagamento PARCELADO
    if (isParcelado) {
      //## VALIDACAO SOMENTE NA PARCELA 1
      if (pagNovo.num_pag == 1) {
        //1 - Alteração TIPO DOCUMENTO
        if (
          fn_isAlteracaoTipoDocPagExigeReaprovacao(
            pagOrig,
            tipoDocNovo,
            tiposDocumento,
          )
        ) {
          gerarLog = true;
          camposLog.tipo_documento = true;
        }

        //2 - Alteração VALOR DOCUMENTO ACIMA DO LIMITE
        const valorTotDocOrig = totaisOrig.valor_documento;
        const valorTotDocNovo = totais.valor_documento;

        if (
          fn_isAlteracaoValorExigeReaprovacao(
            valorTotDocOrig,
            valorTotDocNovo,
            limites.limite_valor,
          )
        ) {
          gerarLog = true;
          camposLog.valor_documento = true;
        }

        //3 - Alteração VALOR PAGAMENTO ACIMA DO LIMITE
        const valorTotPagOrig = totaisOrig.valor_pagamento;
        const valorTotPagNovo = totais.valor_pagamento;

        if (
          fn_isAlteracaoValorExigeReaprovacao(
            valorTotPagOrig,
            valorTotPagNovo,
            limites.limite_valor,
          )
        ) {
          gerarLog = true;
          camposLog.valor_pagamento = true;
        }
      }

      //## VALIDACAO EM TODAS AS PARCELAS
      //4 - DATA VENCIMENTO ACIMA DO LIMITE
      if (
        fn_isAlteracaoVencimentoExigeReaprovacao(
          pagOrig,
          pagNovo,
          limites.limite_vencimento,
        )
      ) {
        gerarLog = true;
        camposLog.data_vencimento = true;
      }
    }
    //Se for pagamento RECORRENTE OU UNICO
    else {
      //1 - Alteração TIPO DOCUMENTO
      if (
        fn_isAlteracaoTipoDocPagExigeReaprovacao(
          pagOrig,
          tipoDocNovo,
          tiposDocumento,
        )
      ) {
        gerarLog = true;
        camposLog.tipo_documento = true;
      }

      //2 - Alteração VALOR DOCUMENTO ACIMA DO LIMITE
      const valorDocOrig = Number(pagOrig.valor_documento);
      const valorDocNovo = Number(pagNovo.valor_documento);

      if (
        fn_isAlteracaoValorExigeReaprovacao(
          valorDocOrig,
          valorDocNovo,
          limites.limite_valor,
        )
      ) {
        gerarLog = true;
        camposLog.valor_documento = true;
      }

      //3 - Alteração VALOR PAGAMENTO ACIMA DO LIMITE
      const valorPagOrig = Number(pagOrig.valor_pagamento);
      const valorPagNovo = Number(pagNovo.valor_pagamento);

      if (
        fn_isAlteracaoValorExigeReaprovacao(
          valorPagOrig,
          valorPagNovo,
          limites.limite_valor,
        )
      ) {
        gerarLog = true;
        camposLog.valor_pagamento = true;
      }

      //4 - DATA VENCIMENTO ACIMA DO LIMITE
      if (
        fn_isAlteracaoVencimentoExigeReaprovacao(
          pagOrig,
          pagNovo,
          limites.limite_vencimento,
        )
      ) {
        gerarLog = true;
        camposLog.data_vencimento = true;
      }
    }
  }

  return { gerarLog, camposLog };
}

//Monta as alterações que geram a reaprovação do pagamento, para a consulta ao lado do aviso da tela da posse.
//Usa as mesmas verificações que decidem a reaprovação (fn_verificarGeracaoLogReaprovacao e o valor fixo)
//@pagamentos: parcelas (só no parcelado, cada uma com o pagamento_orig)
//@pagOrig e @pagNovo: pagamento aprovado e o da tela (recorrente e único)
//@returns [{ num_pag, campo, tipo_valor ("N" número, "S" texto), valor_aprovado, valor_atual, motivo }]
export function fn_getAlteracoesReaprovacaoPagamento({
  isParcelado,
  isValorFixo,
  pagamentos = [],
  pagOrig,
  pagNovo,
  totaisOrig,
  totais,
  tipoDocNovo,
  tiposDocumento,
  limites,
}) {
  const alteracoes = [];

  //Valor maior do que o aprovado: acima do limite da parametrização ou em solicitação de valor fixo
  function addAlteracaoValor(campo, valorAprovado, valorAtual) {
    const aprovado = Number(valorAprovado || 0);
    const atual = Number(valorAtual || 0);
    const acimaLimite = fn_isAlteracaoValorExigeReaprovacao(
      aprovado,
      atual,
      limites.limite_valor,
    );
    const maiorValorFixo = isValorFixo && atual > aprovado;

    if (acimaLimite || maiorValorFixo) {
      alteracoes.push({
        campo,
        tipo_valor: "N",
        valor_aprovado: aprovado,
        valor_atual: atual,
        motivo: acimaLimite
          ? `Aumento de R$ ${fn_formatarNumero(atual - aprovado, 2)}, acima do limite de R$ ${fn_formatarNumero(limites.limite_valor, 2)}`
          : `Aumento de R$ ${fn_formatarNumero(atual - aprovado, 2)} em solicitação de valor fixo`,
      });
    }
  }

  //Tipo documento que passou a não contabilizar
  function addAlteracaoTipoDoc(pagAprovado, pagAtual) {
    if (
      fn_isAlteracaoTipoDocPagExigeReaprovacao(
        pagAprovado,
        tipoDocNovo,
        tiposDocumento,
      )
    ) {
      alteracoes.push({
        campo: "Tipo documento",
        tipo_valor: "S",
        valor_aprovado: tiposDocumento.find(
          (tp) => tp.id == pagAprovado.id_tipo_documento,
        )?.label,
        valor_atual: tipoDocNovo?.label,
        motivo: "Passou para um tipo documento que não contabiliza",
      });
    }
  }

  //Vencimento antecipado além do limite da parametrização
  function addAlteracaoVencimento(pagAprovado, pagAtual, numPag) {
    if (
      fn_isAlteracaoVencimentoExigeReaprovacao(
        pagAprovado,
        pagAtual,
        limites.limite_vencimento,
      )
    ) {
      alteracoes.push({
        num_pag: numPag,
        campo: "Data vencimento",
        tipo_valor: "S",
        valor_aprovado: pagAprovado.data_vencimento,
        valor_atual: pagAtual.data_vencimento,
        motivo: `Antecipado em ${fn_getDifDiasDatas(pagAtual.data_vencimento, pagAprovado.data_vencimento)} dias (limite de ${limites.limite_vencimento} dias)`,
      });
    }
  }

  if (isParcelado) {
    const pagOrig1 = pagamentos[0]?.pagamento_orig;

    //No parcelado o tipo documento e os valores são conferidos na parcela 1, com o total das parcelas
    if (!fn_isEmpty(pagOrig1)) {
      addAlteracaoTipoDoc(pagOrig1, pagamentos[0]);
      addAlteracaoValor(
        "Valor documento (total das parcelas)",
        totaisOrig.valor_documento,
        totais.valor_documento,
      );
      addAlteracaoValor(
        "Valor pagamento (total das parcelas)",
        totaisOrig.valor_pagamento,
        totais.valor_pagamento,
      );
    }

    //O vencimento é conferido em cada parcela
    for (const pag of pagamentos) {
      if (!fn_isEmpty(pag.pagamento_orig)) {
        addAlteracaoVencimento(pag.pagamento_orig, pag, pag.num_pag);
      }
    }
  } else if (!fn_isEmpty(pagOrig)) {
    addAlteracaoTipoDoc(pagOrig, pagNovo);
    addAlteracaoValor(
      "Valor documento",
      pagOrig.valor_documento,
      pagNovo.valor_documento,
    );
    addAlteracaoValor(
      "Valor pagamento",
      pagOrig.valor_pagamento,
      pagNovo.valor_pagamento,
    );
    addAlteracaoVencimento(pagOrig, pagNovo, null);
  }

  return alteracoes;
}

//Retorna a forma de pagamento definida pelo tipo documento, dentre as opções de forma pagamento da tela.
//Retorna null quando o tipo documento não define a forma ou quando ela não está nas opções da tela
//(inativa ou não permitida para o tipo de conta da solicitação): nesses casos a tela funciona como hoje
export function fn_getFormaPagTipoDocumento(
  tipoDocumento,
  opcoesFormaPagamento = [],
) {
  if (!tipoDocumento?.id_forma_pagamento) {
    return null;
  }

  return (
    opcoesFormaPagamento.find(
      (formaPag) => formaPag.value == tipoDocumento.id_forma_pagamento,
    ) || null
  );
}

//Retorna a mensagem de erro quando o tipo documento define a forma pagamento e a forma selecionada é outra
//(caso do pagamento gravado antes de a forma ser definida no tipo documento). Se estiver correto, retorna null
export function fn_getMsgFormaPagDivergeTipoDoc(
  tipoDocumento,
  formaPagTipoDoc,
  formaPagSelecionada,
) {
  if (!formaPagTipoDoc || formaPagSelecionada?.value == formaPagTipoDoc.value) {
    return null;
  }

  return `Para o tipo documento ${tipoDocumento?.nome} a forma de pagamento deve ser ${formaPagTipoDoc.nome || formaPagTipoDoc.label}`;
}

//Retorna o objeto de log para reaprovação do pagamento
export function fn_gerarLogReaprovacaoPagamento(pagOrig, pagNovo) {
  let logsPagamento = [];
  const camposLog = fn_loadCamposLogAprovacao();

  let logOrig = {};
  let logNovo = {};

  //Monta o log de cada campo que precisa logar na aprovação/solicitação de reaprovação
  camposLog.forEach((campoLog) => {
    //Registro dos valores originais
    logOrig.tipo = TIPO_LOG_APROVACAO.ORIGINAL;
    logOrig.num_pag = pagOrig.num_pag;
    logOrig[campoLog.nome_campo] =
      campoLog.tipo_valor == "D" //Data
        ? fn_getData(pagOrig[campoLog.nome_campo])
        : pagOrig[campoLog.nome_campo] || 0;

    //Registro dos novos valores
    logNovo.tipo = TIPO_LOG_APROVACAO.NOVO;
    logNovo.num_pag = pagNovo.num_pag;
    logNovo[campoLog.nome_campo] =
      campoLog.tipo_valor == "D" //Data
        ? fn_getData(pagNovo[campoLog.nome_campo])
        : pagNovo[campoLog.nome_campo] || 0;
  });

  logsPagamento.push(logOrig);
  logsPagamento.push(logNovo);

  return logsPagamento;
}

//Retorna as situações da solicitação que são permitidas apresentar na aba Previsão
export function fn_loadOpcoesSituacaoSolicitacaoAbaPrevisao() {
  const situacoes = [
    SITUACAO_SOLICITACAO.CONTROLE,
    SITUACAO_SOLICITACAO.APROVADA,
    SITUACAO_SOLICITACAO.PENDENTE_REAPROVACAO,
    SITUACAO_SOLICITACAO.REJEITADA_REAPROVACAO,
    SITUACAO_SOLICITACAO.CANCELADA,
  ];

  const opcoes = fn_loadOpcoesSituacaoSolicitacao();
  return opcoes.filter((opcao) => situacoes.includes(opcao.value));
}

//Retorna as opções de filtro da tela gestora de acordo com a aba. Pode ser S (Solicitação) ou P (Previsão/Pagamento)
export function fn_loadOpcoesCampoFiltroGestora(aba) {
  //Filtros comuns para a aba "Solicitação" quanto para a aba "Pevisão" (pagamento)

  let ordem = 1;
  const opcoes = [
    //{ value: "", label: "Nenhum" },

    {
      value: "id_empresa",
      label: "Empresa",
      tabela: "solicitacao",
      tipo_campo: "SEL",
      ordem: ordem++,
    },
    {
      value: "id_fornecedor",
      label: "Fornecedor",
      tabela: "solicitacao",
      tipo_campo: "SEL_ASYNC",
      ordem: ordem++,
    },
    {
      value: "descricao",
      label: "Descrição",
      tabela: "solicitacao",
      tipo_campo: "TXT",
      ordem: ordem++,
    },
    {
      value: "id_usuario",
      label: "Cadastrado por",
      tabela: "solicitacao",
      tipo_campo: "SEL_ASYNC",
      ordem: ordem++,
    },
    {
      value: "nome_negociador",
      label: "Negociador",
      tabela: "solicitacao",
      tipo_campo: "TXT",
      ordem: ordem++,
    },
    {
      value: "tipo_pagamento",
      label: "Tipo pagamento",
      tabela: "solicitacao",
      tipo_campo: "SEL",
      ordem: ordem++,
    },
    {
      value: "tipo_valor_pagamento",
      label: "Tipo valor pagamento",
      tabela: "solicitacao",
      tipo_campo: "SEL",
      ordem: ordem++,
    },
    {
      value: "tipo_apropriacao",
      label: "Tipo apropriação",
      tabela: "solicitacao",
      tipo_campo: "SEL",
      ordem: ordem++,
    },
  ];

  //Filtros somente para a aba Solicitação
  if (aba == "S") {
    opcoes.push({
      value: "operacao_realizada",
      label: "Operação realizada",
      tabela: null,
      tipo_campo: "SEL",
      ordem: ordem++,
    });
  }

  //Filtros somente para a aba Previsão / Pagamento
  if (aba == "P") {
    opcoes.push(
      {
        //Quantidade de contas do valor documento no rateio do pagamento (sem as de desconto, despesa, multa e juros)
        value: "qtde_conta",
        label: "Qtde conta valor doc",
        tabela: null,
        tipo_campo: "TXT",
        tipo_valor: "N",
        ordem: ordem++,
      },
      {
        //Pagamentos com a conta no rateio, podendo ter outras contas
        value: "id_conta_contem",
        label: "Conta (contém)",
        tabela: null,
        tipo_campo: "SEL",
        ordem: ordem++,
      },
      {
        value: "id_centro_custo_contem",
        label: "Centro de custo (contém)",
        tabela: null,
        tipo_campo: "SEL",
        ordem: ordem++,
      },
      {
        value: "id_centro_custo_unico",
        label: "Centro de custo (único)",
        tabela: null,
        tipo_campo: "SEL",
        ordem: ordem++,
      },
      {
        value: "tipo_rateio",
        label: "Tipo rateio",
        tabela: null,
        tipo_campo: "SEL",
        ordem: ordem++,
      },
      {
        value: "tipo_documento",
        label: "Tipo documento",
        tabela: null,
        tipo_campo: "SELMULTI",
        ordem: ordem++,
      },
      {
        value: "id_forma_pagamento",
        label: "Forma pagamento",
        tabela: null,
        tipo_campo: "SELMULTI",
        ordem: ordem++,
      },
      {
        value: "num_pag",
        label: "Num. pagamento",
        tabela: null,
        tipo_campo: "TXT",
        tipo_valor: "N",
        ordem: ordem++,
      },
      {
        value: "num_documento",
        label: "Num. documento",
        tabela: null,
        tipo_campo: "TXT",
        ordem: ordem++,
      },
      {
        value: "data_posse",
        label: "Posse pendente",
        tabela: null,
        tipo_campo: "SEL",
        ordem: ordem++,
      },
      {
        value: "valor_desconto",
        label: "Desconto",
        tabela: null,
        tipo_campo: "SEL",
        ordem: ordem++,
      },
      {
        value: "valor_devolucao",
        label: "Devolução",
        tabela: null,
        tipo_campo: "SEL",
        ordem: ordem++,
      },
      {
        value: "valor_despesa",
        label: "Despesa",
        tabela: null,
        tipo_campo: "SEL",
        ordem: ordem++,
      },
      {
        value: "valor_juros",
        label: "Juros",
        tabela: null,
        tipo_campo: "SEL",
        ordem: ordem++,
      },
      {
        value: "valor_multa",
        label: "Multa",
        tabela: null,
        tipo_campo: "SEL",
        ordem: ordem++,
      },
      {
        value: "valor_imposto",
        label: "Imposto",
        tabela: null,
        tipo_campo: "SEL",
        ordem: ordem++,
      },

      {
        value: "situacao",
        label: "Situação solicitação",
        tabela: "solicitacao",
        tipo_campo: "SEL",
        ordem: ordem++,
      },

      //Filtros anexo pagamento (um para cada tipo de arquivo)
      {
        value: "upload_doc",
        label: "Upload documento",
        tabela: null,
        tipo_campo: "SEL",
        ordem: ordem++,
      },
      {
        value: "upload_cpl",
        label: "Upload doc. complementar",
        tabela: null,
        tipo_campo: "SEL",
        ordem: ordem++,
      },
      {
        value: "upload_fpg",
        label: "Upload forma pag.",
        tabela: null,
        tipo_campo: "SEL",
        ordem: ordem++,
      },
    );
  }

  return opcoes;
}

//Ordenação das opções de centro de custo do select com checkbox "TODOS xxx" (parâmetro order da listagem):
//primeiro os centros com empresa (loja), pelo código da empresa; depois os demais pelo tipo (ADM, CD, pagamento); e pelo nome.
//Assim os centros da mesma empresa e do mesmo tipo ficam juntos.
export const ORDEM_OPCOES_CENTRO_CUSTO_CHECK =
  "empresa,codigo,ASC;tipo,ASC;nome,ASC";

/*
 * Carrega as opções de centro de custo e adiciona as opções com checkbox "TODOS xxx" no início,
 * na mesma ordem dos centros (ORDEM_OPCOES_CENTRO_CUSTO_CHECK): primeiro os de loja de cada empresa,
 * pelo código da empresa; depois o de ADM e o de CD
 */
export async function fn_loadOpcoesCentroCustoCheck(
  opcoesCentro,
  opcoesEmpresa,
) {
  const opcoesTodos = [];

  //Empresas pelo código (a empresa sem código fica no final)
  const empresasPorCodigo = [...opcoesEmpresa].sort(
    (a, b) =>
      (a.codigo == null) - (b.codigo == null) ||
      Number(a.codigo) - Number(b.codigo),
  );

  //1 - Adiciona uma opção de todos os centros de LOJA para cada empresa que possui filial loja
  for (const empresa of empresasPorCodigo) {
    const filiaisLoja = await api_listar(
      ROTA_LISTAR.FILIAL,
      `situacao=A&id_empresa=${empresa.value}&tipo=${TIPO_FILIAL.LOJA}`,
    );

    if (filiaisLoja.length > 0) {
      opcoesTodos.push({
        isAction: true,
        id_empresa: empresa.value,
        tipo: TIPO_CENTRO_CUSTO.LOJA,
        value: empresa.value,
        label: `Todos centros loja ${empresa.nome}`,
      });
    }
  }

  //2 - Adiciona opção de todos os centros ADM
  const existeCentrosADM = opcoesCentro.some(
    (centro) => centro.tipo == TIPO_CENTRO_CUSTO.ADMINISTRATIVO,
  );

  if (existeCentrosADM) {
    opcoesTodos.push({
      isAction: true,
      id_empresa: null,
      tipo: TIPO_CENTRO_CUSTO.ADMINISTRATIVO,
      value: null,
      label: "Todos centros ADM",
    });
  }

  //3 - Adiciona opção de todos os centros CD
  const existeCentrosCD = opcoesCentro.some(
    (centro) => centro.tipo == TIPO_CENTRO_CUSTO.CENTRO_DISTRIBUICAO,
  );

  if (existeCentrosCD) {
    opcoesTodos.push({
      isAction: true,
      id_empresa: null,
      tipo: TIPO_CENTRO_CUSTO.CENTRO_DISTRIBUICAO,
      value: null,
      label: "Todos centros CD",
    });
  }

  return [...opcoesTodos, ...opcoesCentro];
}

//Adiciona na lista os centros inativos que já estão selecionados na solicitação
export function fn_adicionarCentrosInativosEdicao(
  opcoesCentro,
  solicitacaoEdit,
  somenteComValor = true,
) {
  //Não pega rateio de pagamentos conferidos
  const centrosRateio = fn_getCentrosRateioPagamentos(
    solicitacaoEdit.pagamentos.filter(
      (pag) => pag.situacao != SITUACAO_PAGAMENTO.CONFERIDO,
    ),
    somenteComValor,
  );

  const centrosSolicitacao = solicitacaoEdit.centros
    .filter((solCentro) => solCentro.id_centro_custo)
    .map((solCentro) => solCentro.centro);

  const centrosAdicionar = [...centrosSolicitacao, ...centrosRateio]
    .filter(
      (centro, index, array) =>
        array.findIndex((c) => c.value == centro.value) == index,
    )
    .filter(
      (centro) =>
        !opcoesCentro.some((o) => !o.isAction && o.value == centro.value),
    )
    .map((centro) => ({
      ...centro,
      ...(centro.situacao == "I" ? { inativo: true } : {}),
    }));

  return [...opcoesCentro, ...centrosAdicionar];
}

//Monta os centros da solicitação para posterior gravação na tabela SOLICITACAO_CENTROS
export function fn_montarCentrosSolicitacao(
  opcoesCentroCusto,
  selectedCentros,
) {
  const centrosSolicitacao = [];

  const empresasComTodos = [];
  const tiposComTodos = [];

  //1 - Verifica empresas com todos centros loja selecionados
  //Obtém as distintas empresas
  const empresas = [
    ...new Set(
      opcoesCentroCusto
        .filter((o) => !o.isAction && !o.inativo && o.id_empresa != null)
        .map((o) => o.id_empresa),
    ),
  ];

  //Para cada empresa, obtém os centros ativos da empresa e verifica se todos estão selecionados
  empresas.forEach((idEmpresa) => {
    const centrosDaEmpresa = opcoesCentroCusto.filter(
      (o) =>
        !o.isAction &&
        !o.inativo &&
        o.situacao != "I" &&
        o.id_empresa == idEmpresa,
    );

    const todosSelecionados =
      centrosDaEmpresa.length > 0 &&
      centrosDaEmpresa.every((c) =>
        selectedCentros.some((s) => s.value == c.value && s.situacao != "I"),
      );

    if (todosSelecionados) {
      empresasComTodos.push(idEmpresa);

      centrosSolicitacao.push({
        tipo: "T", //todos loja
        id_empresa: idEmpresa,
        id_centro_custo: null,
      });
    }
  });

  //2 - Verifica todos centros do tipo ADM e do tipo CD
  const tiposTodos = [
    {
      tipoCentro: TIPO_CENTRO_CUSTO.ADMINISTRATIVO,
      tipoSelCentros: "TA",
    },
    {
      tipoCentro: TIPO_CENTRO_CUSTO.CENTRO_DISTRIBUICAO,
      tipoSelCentros: "TC",
    },
  ];

  tiposTodos.forEach(({ tipoCentro, tipoSelCentros }) => {
    const centrosDoTipo = opcoesCentroCusto.filter(
      (o) =>
        !o.isAction &&
        !o.inativo &&
        o.situacao != "I" && //novo 17/09/26
        o.id_empresa == null &&
        o.tipo == tipoCentro,
    );

    const todosSelecionados =
      centrosDoTipo.length > 0 &&
      centrosDoTipo.every((c) =>
        selectedCentros.some((s) => s.value == c.value && s.situacao != "I"),
      );

    if (todosSelecionados) {
      tiposComTodos.push(tipoCentro);

      centrosSolicitacao.push({
        tipo: tipoSelCentros,
        id_empresa: null,
        id_centro_custo: null,
      });
    }
  });

  //3 - Adiciona centros individuais
  const centrosIndividuais = selectedCentros.filter((centro) => {
    const estaAtivo = !centro.inativo && centro.situacao != "I";

    const empresaFoiSelecionadaComoTodos =
      estaAtivo &&
      centro.id_empresa != null &&
      empresasComTodos.some(
        (idEmp) => Number(idEmp) == Number(centro.id_empresa),
      );

    const tipoFoiSelecionadoComoTodos =
      estaAtivo &&
      centro.id_empresa == null &&
      tiposComTodos.some((tipo) => tipo == centro.tipo);

    return !empresaFoiSelecionadaComoTodos && !tipoFoiSelecionadoComoTodos;
  });

  centrosIndividuais.forEach((centro) => {
    centrosSolicitacao.push({
      id_empresa: null,
      id_centro_custo: centro.value,
      tipo: null,
    });
  });

  return centrosSolicitacao;
}

/*
 * Retorna o grupo da opção de centro de custo no select com as opções "Todos" (getGrupoOpcao do USelect com showGrupoCheck):
 * o centro inativo fica no grupo "Inativos"; o de loja, no grupo com o nome da empresa; os demais, no nome do tipo
 */
export function fn_getGrupoOpcaoCentroCusto(opcao) {
  if (opcao.isAction) {
    return null;
  }

  if (opcao.inativo) {
    return "Inativos";
  }

  if (opcao.tipo == TIPO_CENTRO_CUSTO.LOJA && opcao.empresa?.nome) {
    return opcao.empresa.nome;
  }

  return fn_getTipoCentroCusto(opcao.tipo)?.label || null;
}

//Retorna as opções do select com check
export function fn_getOpcoesSelectCheck(opcoes, selectedOptions) {
  return opcoes.map((opt) => {
    if (!opt.isAction) return opt;

    let opcoesFiltro;

    if (opt.id_empresa != null) {
      opcoesFiltro = opcoes.filter(
        (o) => !o.isAction && !o.inativo && o.id_empresa == opt.id_empresa,
      );
    } else {
      opcoesFiltro = opcoes.filter(
        (o) =>
          !o.isAction &&
          !o.inativo &&
          o.id_empresa == null &&
          o.tipo == opt.tipo,
      );
    }

    const todosSelecionados =
      opcoesFiltro.length > 0 &&
      opcoesFiltro.every((c) =>
        selectedOptions.some((s) => s.value == c.value),
      );

    return {
      ...opt,
      isChecked: todosSelecionados,
    };
  });
}

//Carrega a seleção dos centros de custo no select para edição
export function fn_carregarSelecaoCentrosEdicao(solicitacaoEdit, opcoesCentro) {
  const centrosIndividuais = solicitacaoEdit.centros
    .filter((solCentro) => solCentro.id_centro_custo)
    .map((solCentro) => solCentro.centro);

  const centrosIndividuaisAtivos = centrosIndividuais.filter((centro) =>
    opcoesCentro.some((o) => !o.isAction && o.value == centro.value),
  );

  const centrosInativos = centrosIndividuais
    .filter(
      (centro) =>
        !opcoesCentro.some((o) => !o.isAction && o.value == centro.value),
    )
    .map((centro) => ({
      ...centro,
      inativo: true,
    }));

  const empresasTodos = solicitacaoEdit.centros
    .filter(
      (solCentro) => solCentro.tipo == "T" && solCentro.id_empresa != null,
    )
    .map((solCentro) => solCentro.id_empresa);

  const tiposTodos = solicitacaoEdit.centros
    .filter((solCentro) => solCentro.tipo == "TA" || solCentro.tipo == "TC")
    .map((solCentro) => solCentro.tipo);

  //Verifica se existe uma gravação antiga de todos os centros sem empresa
  const todosCentrosSemEmpresa = solicitacaoEdit.centros.some(
    (solCentro) =>
      solCentro.tipo == "A" &&
      solCentro.id_empresa == null &&
      solCentro.id_centro_custo == null,
  );

  const centrosEmpresa = opcoesCentro.filter(
    (o) =>
      !o.isAction &&
      !o.inativo &&
      o.id_empresa != null &&
      empresasTodos.includes(o.id_empresa),
  );

  const centrosTipo = opcoesCentro.filter((o) => {
    if (o.isAction || o.inativo || o.id_empresa != null) {
      return false;
    }

    if (
      tiposTodos.includes("TA") &&
      o.tipo == TIPO_CENTRO_CUSTO.ADMINISTRATIVO
    ) {
      return true;
    }

    if (
      tiposTodos.includes("TC") &&
      o.tipo == TIPO_CENTRO_CUSTO.CENTRO_DISTRIBUICAO
    ) {
      return true;
    }

    return false;
  });

  //Centros sem empresa gravados pelo formato antigo "A"
  const centrosTipoAntigo = opcoesCentro.filter(
    (o) =>
      !o.isAction &&
      !o.inativo &&
      o.id_empresa == null &&
      todosCentrosSemEmpresa,
  );

  const centrosRateio = fn_getCentrosRateioPagamentos(
    solicitacaoEdit.pagamentos.filter(
      (pag) => pag.situacao != SITUACAO_PAGAMENTO.CONFERIDO,
    ),
    true, //com valor > 0
  );

  const map = new Map();

  [
    ...centrosIndividuaisAtivos,
    ...centrosEmpresa,
    ...centrosTipo,
    ...centrosTipoAntigo,
    ...centrosInativos,
    ...centrosRateio,
  ].forEach((o) => map.set(o.value, o));

  return Array.from(map.values());
}

//Ação da seleção do check do select de centro de custo
export function fn_handleSelecaoCheckCentroCusto(
  actionOption,
  opcoesCentroCusto,
  selectedCentros,
) {
  const { id_empresa, tipo } = actionOption;

  let centrosDaAcao;

  if (id_empresa != null) {
    //Todos os centros da empresa
    centrosDaAcao = opcoesCentroCusto.filter(
      (o) => !o.isAction && o.id_empresa == id_empresa && o.situacao == "A",
    );
  } else {
    //Todos os centros do tipo (ADM ou CD)
    centrosDaAcao = opcoesCentroCusto.filter(
      (o) =>
        !o.isAction &&
        o.id_empresa == null &&
        o.tipo == tipo &&
        o.situacao == "A",
    );
  }

  const valoresCentrosAcao = centrosDaAcao.map((o) => o.value);

  const jaSelecionados =
    valoresCentrosAcao.length > 0 &&
    valoresCentrosAcao.every((v) => selectedCentros.some((s) => s.value == v));

  let novoSelectedCentros;

  if (jaSelecionados) {
    //DESMARCOU → remove somente os centros dessa ação
    novoSelectedCentros = selectedCentros.filter(
      (s) => !valoresCentrosAcao.includes(s.value),
    );
  } else {
    //MARCOU → adiciona os centros dessa ação mantendo os outros
    const map = new Map();

    [...selectedCentros, ...centrosDaAcao].forEach((o) => map.set(o.value, o));

    novoSelectedCentros = Array.from(map.values());
  }

  return novoSelectedCentros;
}

//Retorna o array centrosTodos que contém a descrição da seleção do "TODOS" e
//um array centrosIndividuais que contém o objeto centro dos centros selecionados individualmente
export function fn_loadApresentacaoSelecaoCentros(centrosSolicitacao) {
  const centrosTodos = [];
  const centrosIndividuais = [];

  for (const solCentro of centrosSolicitacao) {
    //Se selecionou TODOS centros ADM
    if (solCentro.id_empresa == null && solCentro.tipo == "TA") {
      centrosTodos.push("TODOS ADM");
    }
    //Se selecionou TODOS centros CD
    else if (solCentro.id_empresa == null && solCentro.tipo == "TC") {
      centrosTodos.push("TODOS CD");
    }
    //Se selecionou TODOS centros de alguma empresa
    else if (solCentro.id_empresa != null && solCentro.tipo == "T") {
      centrosTodos.push(`TODOS ${solCentro.empresa?.nome} `);
    }
    //Se selecionou TODOS centros sem empresa (ADM,CD,PAG)
    else if (solCentro.id_empresa == null && solCentro.tipo == "A") {
      centrosTodos.push("TODOS, EXCETO LOJA");
    }
    //Se selecionou centro de custo individual
    else if (solCentro.id_centro_custo) {
      centrosIndividuais.push(solCentro.centro);
    }
  }

  centrosTodos.sort((a, b) => a.localeCompare(b));

  return { centrosTodos, centrosIndividuais };
}

//Retorna as distintas contas e centros do rateio do pagamento
export function fn_getOpcoesContasCentrosRateioPagOrig(
  pag,
  opcoesConta,
  opcoesCentroCusto,
) {
  //Guarda os centros e contas originais
  const idsContas = Object.keys(pag.rateio.valores_contas).map((id) =>
    Number(id),
  );

  const idsCentros = Object.values(pag.rateio.rateio_centros_conta)
    .flatMap((conta) => Object.keys(conta.valores_centros || {}))
    .map((id) => Number(id));

  const opcoesContasOrig = opcoesConta.filter((conta) =>
    idsContas.includes(Number(conta.value)),
  );

  const opcoesCentrosOrig = opcoesCentroCusto.filter(
    (centro) => idsCentros.includes(Number(centro.value)) && !centro.isAction,
  );

  return { opcoesContasOrig, opcoesCentrosOrig };
}

//Retorna as distintas contas e centros do rateio de determinado pagamento
export function fn_getOpcoesContasCentrosRateioValorDocPagOrigBD(pagamento) {
  const rateiosValorDoc = pagamento.rateio.filter(
    (rateio) => rateio.tipo_valor_conta == null,
  );

  const opcoesContasOrig = [
    ...new Map(
      rateiosValorDoc.map((rateio) => [
        rateio.conta.id,
        {
          ...rateio.conta,
          label: rateio.conta.nome,
          value: rateio.conta.id,
        },
      ]),
    ).values(),
  ];

  const opcoesCentrosOrig = [
    ...new Map(
      rateiosValorDoc.map((rateio) => [
        rateio.centro_custo.id,
        {
          ...rateio.centro_custo,
          label: rateio.centro_custo.nome,
          value: rateio.centro_custo.id,
        },
      ]),
    ).values(),
  ];

  return { opcoesContasOrig, opcoesCentrosOrig };
}

/*
 * Retorna o aviso da tela da posse quando o rateio automático recalculado (o que a posse grava) está diferente do gravado,
 * sem os detalhes da diferença. Sem aviso quando não houver diferença.
 * - Posse ainda não salva: só avisa que o rateio foi recalculado.
 * - Posse já salva: pede para salvar de novo, para gravar o rateio recalculado.
 * - Sem a qtde colaborador / faturamento da posição: o aviso para cadastrar a informação (a posse não consegue gravar).
 * O pagamento gravado antes da posição do rateio (posição vazia, preenchida pela carga do SQL de produção) só tem aviso
 * quando o rateio dos centros for diferente.
 * @comparacao: retorno da api_compararRateioAtualizado. @isPosseSalva: o pagamento já tem a posse gravada
 */
export function fn_getAvisoRateioRecalculadoPosse(comparacao, isPosseSalva) {
  if (!comparacao?.divergente) return null;

  if (comparacao.sem_informacao) {
    return fn_getAvisoRateioAtualizado(comparacao);
  }

  const {
    posicao_gravada,
    posicao_atual,
    centros_novos = 0,
    centros_retirados = 0,
    centros_valor_alterado = 0,
  } = comparacao;

  const possuiDiferenca =
    (posicao_gravada && posicao_gravada != posicao_atual) ||
    centros_novos > 0 ||
    centros_retirados > 0 ||
    centros_valor_alterado > 0;

  if (!possuiDiferenca) return null;

  return isPosseSalva
    ? "O rateio automático foi recalculado nesta tela com os dados atualizados. Confira e salve a posse novamente para gravar o rateio."
    : "O rateio automático foi recalculado nesta tela com os dados atualizados.";
}

//Retorna o aviso de que o rateio automático gravado está diferente do rateio atualizado (o que a posse grava),
//com os motivos, para salvar a posse (conferência). Sem aviso quando não houver divergência
//@comparacao: retorno da api_compararRateioAtualizado
export function fn_getAvisoRateioAtualizado(comparacao) {
  if (!comparacao?.divergente) return null;

  const {
    posicao_gravada,
    posicao_atual,
    sem_informacao,
    centros_novos = 0,
    centros_retirados = 0,
    centros_valor_alterado = 0,
  } = comparacao;

  if (sem_informacao) {
    return `Rateio automático sem informação (qtde colaborador / faturamento) na posição ${posicao_atual}: cadastre a informação para salvar a posse.`;
  }

  const motivos = [];
  if (posicao_gravada != posicao_atual) {
    motivos.push(
      `posição ${posicao_gravada || "não gravada"} → ${posicao_atual}`,
    );
  }
  if (centros_novos > 0) {
    motivos.push(
      centros_novos == 1 ? "1 centro novo" : `${centros_novos} centros novos`,
    );
  }
  if (centros_retirados > 0) {
    motivos.push(
      centros_retirados == 1
        ? "1 centro retirado"
        : `${centros_retirados} centros retirados`,
    );
  }
  if (centros_valor_alterado > 0) {
    motivos.push(
      centros_valor_alterado == 1
        ? "valor alterado em 1 centro"
        : `valor alterado em ${centros_valor_alterado} centros`,
    );
  }

  return `Rateio automático gravado diferente do atualizado (${motivos.join(", ")}): salve a posse para gravar o rateio atualizado.`;
}

//----- SERÃO ELIMINADOS DEPOIS
// //Carrega as opções de centro de custo com as opções com checkbox "TODOS xxx"
// export async function fn_loadOpcoesCentroCustoCheck(
//   opcoesCentroFull,
//   opcoesEmpresa,
// ) {
//   const opcoesCentroCheck = [...opcoesCentroFull];

//   //Acdiciona uma opção de todos os centros de cada empresa
//   for (const empresa of opcoesEmpresa) {
//     const filiais = await api_listar(
//       ROTA_LISTAR.FILIAL,
//       `id_empresa=${empresa.value}&tipo=${TIPO_FILIAL.LOJA}`,
//     );

//     if (filiais.length > 0) {
//       opcoesCentroCheck.unshift({
//         isAction: true,
//         id_empresa: empresa.value,
//         value: empresa.value,
//         label: `Todos centros loja da empresa ${empresa.nome}`,
//       });
//     }
//   }

//   //Adiciona a opção de todos os centros ADM
//   opcoesCentroCheck.unshift({
//     isAction: true,
//     id_empresa: null,
//     value: null,
//     label: "Todos centros ADM/CD",
//   });

//   return opcoesCentroCheck;
// }

// //Monta os centros da solicitação para posterior gravação na tabela SOLICITACAO_CENTROS
// export function fn_montarCentrosSolicitacao(
//   opcoesCentroCusto,
//   selectedCentros,
// ) {
//   const centrosSolicitacao = [];

//   // Descobre empresas existentes
//   const empresas = [
//     ...new Set(
//       opcoesCentroCusto.filter((o) => !o.isAction).map((o) => o.id_empresa),
//     ),
//   ];

//   const empresasComTodos = [];

//   //1 - Verifica empresas com todos centros selecionados
//   empresas.forEach((idEmpresa) => {
//     const centrosDaEmpresa = opcoesCentroCusto.filter(
//       (o) => !o.isAction && o.id_empresa == idEmpresa,
//     );

//     const todosSelecionados = centrosDaEmpresa.every((c) =>
//       selectedCentros.some((s) => s.value == c.value),
//     );

//     if (todosSelecionados) {
//       empresasComTodos.push(idEmpresa);

//       centrosSolicitacao.push({
//         id_empresa: idEmpresa,
//         id_centro_custo: null,
//         tipo: idEmpresa == null ? "A" : "T",
//       });
//     }
//   });

//   //2 -  Adiciona centros individuais (das empresas que NÃO estão como todos)
//   const centrosIndividuais = selectedCentros.filter(
//     (centro) => !empresasComTodos.includes(centro.id_empresa),
//   );

//   centrosIndividuais.forEach((centro) => {
//     centrosSolicitacao.push({
//       id_empresa: null,
//       id_centro_custo: centro.value,
//       tipo: null,
//     });
//   });

//   return centrosSolicitacao;
// }
//--------------

///VER SE VAI PRECISAR DEPOIS
// function getCentrosRateioPagamento(pagamento, selectedCentros) {
//   const centrosRateio = Object.values(
//     pagamento.rateio?.rateio_centros_conta || {},
//   )
//     .flatMap((conta) => Object.keys(conta.valores_centros || {}))
//     .map((id) => String(id));

//   const centros = [...selectedCentros];

//   centrosRateio.forEach((idCentro) => {
//     const existe = centros.some((centro) => String(centro.value) == idCentro);

//     if (!existe) {
//       const centro = opcoesCentroFull.find(
//         (item) => String(item.value) == idCentro,
//       );

//       if (centro) {
//         centros.push(centro);
//       }
//     }
//   });

//   return centros;
// }

/*
 * Mostra a mensagem do erro do mês/ano ref. do pagamento (código de fn_getErroMesAnoRefPagamento).
 * Usada na conferência ao sair do campo e ao salvar, para as duas mensagens serem iguais.
 * @erro: código ERRO_MES_ANO_REF
 * @mesAnoInicioVigencia: início da vigência do contrato (MM/YYYY), apresentado no erro ANTERIOR_VIGENCIA
 */
export function fn_showMensagemErroMesAnoRefPagamento(
  erro,
  mesAnoInicioVigencia,
) {
  if (erro == ERRO_MES_ANO_REF.INVALIDO) {
    showMensagemErro(MENSAGENS_ERRO.MESANO_INVALIDO);
  } else if (erro == ERRO_MES_ANO_REF.POSTERIOR_EMISSAO) {
    showMensagemErro(
      MENSAGENS_ERRO.MESANO_X_ANTERIOR_OU_IGUAL_MESANO_Y,
      "ref.",
      "emissão",
    );
  } else if (erro == ERRO_MES_ANO_REF.ANTERIOR_VIGENCIA) {
    showMensagemErro(
      MENSAGENS_ERRO.MESANO_X_POSTERIOR_OU_IGUAL_MESANO_Y,
      "ref.",
      `do início da vigência contrato.\nInício: ${mesAnoInicioVigencia}`,
    );
  }
}
